import React, { useState } from 'react';

import {
  QAlert,
  QBox,
  QButton,
  QCenter,
  QCloseButton,
  QFormControl,
  QHeading,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QRadio,
  QRadioGroup,
  QSpinner,
  QStack,
  QText,
} from '@qualio/ui-components';
import { FormikProps } from 'formik';
import { useConfigsQuery } from 'hooks';
import { moveFilesToAdditionalDocuments } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { AddSupplierFormikPayload, ConfigTypeOptions } from 'v2types';

type RiskAssessmentFormProps = {
  formik: FormikProps<AddSupplierFormikPayload>;
};

export const RiskAssessmentForm: React.FC<RiskAssessmentFormProps> = ({ formik }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, errors, touched } = formik;
  const { riskTypeOptions, isConfigsLoading } = useConfigsQuery();
  const [currentRiskType, setCurrentRiskType] = useState<ConfigTypeOptions>();
  const [showModal, setShowModal] = useState(false);

  const isFieldInvalid = (field: keyof AddSupplierFormikPayload) => {
    return !!touched && !!touched[field] && !!errors[field];
  };

  const updateRiskType = () => {
    setFieldValue('riskType', currentRiskType?.payload);
    setFieldValue('documents', moveFilesToAdditionalDocuments(values.documents));
    setFieldValue('supplierTypeOrRiskUpdated', true);
    setShowModal(false);
  };

  return (
    <QBox maxWidth="640px" mb={2}>
      <QHeading mb={2}>{t('addSupplier.riskAssessment.heading')}</QHeading>
      {isConfigsLoading ? (
        <QCenter p={5}>
          <QSpinner />
        </QCenter>
      ) : (
        <>
          <QText fontSize="sm" color="gray.500" pb={6}>
            {t('addSupplier.riskAssessment.description')}
          </QText>
          <QBox data-cy="risk-type-title">
            <QRadioGroup name="riskType" value={values.riskType?.id}>
              <QStack spacing={4}>
                {isFieldInvalid('riskType') ? <QAlert status="error" description={String(errors.riskType)} /> : null}
                {!!riskTypeOptions &&
                  riskTypeOptions.map((riskType) => (
                    <QFormControl key={`riskType-${riskType.value}`} isInvalid={isFieldInvalid('riskType')}>
                      <QRadio
                        onChange={() => {
                          if (values.documents.length!) {
                            if (values.documents.length > 1 && values.documents[0].files.length) {
                              setCurrentRiskType(riskType);
                              setShowModal(true);
                            } else {
                              setFieldValue('riskType', riskType.payload);
                            }
                          } else {
                            setFieldValue('riskType', riskType.payload);
                          }
                        }}
                        value={riskType.value}
                      >
                        {riskType.label}
                      </QRadio>
                      <QText fontSize="sm" color="gray.500">
                        {riskType.payload?.description}
                      </QText>
                    </QFormControl>
                  ))}
              </QStack>
            </QRadioGroup>
          </QBox>
        </>
      )}

      <QModal onClose={() => setShowModal(false)} size="md" isOpen={showModal}>
        <QModalHeader>
          <QText>{t('addSupplier.modals.changeRiskLevel.title')}</QText>
          <QCloseButton onClick={() => setShowModal(false)} />
        </QModalHeader>
        <QModalBody>
          <div data-cy="change-risk-level-modal-text">{t('addSupplier.modals.changeRiskLevel.message')}</div>
        </QModalBody>
        <QModalActions>
          <QButton isDestructive onClick={() => setShowModal(false)} variant="outline">
            {t('addSupplier.modals.changeRiskLevel.buttons.cancel')}
          </QButton>
          <QButton onClick={updateRiskType} data-cy="change-risk-level">
            {t('addSupplier.modals.changeRiskLevel.buttons.change')}
          </QButton>
        </QModalActions>
      </QModal>
    </QBox>
  );
};
